<template>
<div class="export-ecdc-data">
  <h2>ECDC «{{ dataset }}» </h2>
  <alert type="info">
    <p><strong>Attention !</strong></p>
    <p>
      Seules les fiches de la modalité <strong>reaInfADI</strong>
      sont exportées.
    </p>
    <p>
      Les fichiers à transmettre sont téléchargés au <strong>format CSV</strong> et compressés dans une <strong>archive .zip</strong>.
    </p>
  </alert>
  <btn size="lg"
      class="export-button"
      @click="getData"
      type="ecdc"
      :disabled="waiting">
    ECDC
  </btn>
  <save-blob :target="target"></save-blob>
</div>
</template>
<script>
import stringify from 'csv-stringify'
import saveBlob from '@/sui/components/saveblob'
import {
  flatten,
  bsiOriginKT,
  includeInRetrospectiveSurv
} from '@/sui/data/flatten/ecdc'
import assert from '@/sui/tools/assert'

function fileName (key, idx, year) {
  return `${idx}.HAI${key}_${year}.csv`
}

export default {
  name: 'ecdc-export',

  components: {
    'save-blob': saveBlob
  },

  props: {
    year: {
      type: String,
      required: true
    },

    dataset: {
      type: String,
      required: true
    },

    surveillances: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      target: null,
      active: false
    }
  },

  computed: {
    waiting: function () {
      return this.active || this.surveillances.length === 0
    },

    intYear: function () {
      return parseInt(this.year)
    }
  },

  methods: {
    getData: function () {
      const requestSheets = {
        mod: 'reaInfADI',
        name: this.dataset,
        step: 2
      }
      const vm = this
      this.active = true
      const result = {}

      const survIds = []
      const surv = this.surveillances[this.year]
      for (let i = 0; i !== surv.length; ++i) {
        survIds.push(surv[i].id)
      }

      const requestDataset = {
        name: this.dataset,
        mod: 'reaInfADI',
        step: 2
      }

      Promise.all([
        this.$store.dispatch('dataset/sheets', requestSheets),
        this.$store.dispatch('dataset/all', requestDataset)
      ]).then(function (r) {
        const l = ['sh', 'ds']
        const err = [
          'Aucune fiche réaInfADI présente dans la base',
          'Aucun dataset présent dans la base'
        ]

        for (let i = 0; i < r.length; ++i) {
          const data = r[i].data.v
          if (data == null || data.length === 0) {
            vm.setError(err[i])
            return
          }

          result[l[i]] = data
        }

        setTimeout(
          () => vm.exportList(result),
          100
        )
      }).catch(function (error) {
        vm.setError(error)
      })
    },

    exportList: function (r) {
      const vm = this

      const surv = this.surveillances[this.year].slice().sort(
        (f, s) => f.id.localeCompare(s.id)
      )

      const datasets = r.ds.slice().filter(
        d => d.mod === 'reaInfADI'
      ).sort((f, s) => f.surv.localeCompare(s.surv))

      const rawSheets = r.sh.slice().filter(
        s => s.v > 0
      ).sort((f, s) => f.sv.localeCompare(s.sv))

      const sheets = []
      const tags = []
      rawSheets.forEach(
        l => {
          const uf = l.f.uf.trim()
          const unit = l.f.unitType

          const ds = datasets.find(d => d.surv === l.sv)
          const beds = ds.den.find(
            di => di.den === 'bed' && di.ut === unit && di.uf.trim() === uf
          )

          const bd = l.f.birthdate
          const sex = l.f.sex
          const etd = l.f.entry
          const exd = l.f.exit
          const dth = l.f.death
          const org = l.f.org
          const trm = l.f.trauma
          const dg = l.f.diagnosis
          const imm = l.f.cancer
          const can = l.f.immuno
          const atb = l.f.antibio
          const igs = l.f.mainSeverity.value
          const itb = l.f.intubStart
          const uri = l.f.urinaryStart

          if (!includeInRetrospectiveSurv(ds, unit, uf, exd, dth, l.f.dead)) {
            return
          }

          const kts = []
          const ktTags = []
          l.f.catheters.forEach(
            k => {
              const tp = bsiOriginKT(k.type_v2, k.isColIlcBlc)
              const ins = k.insertion
              const rem = k.removal
              const inss = k.insertionSite
              const eps = k.colIlcBlc
              const dd = k.diagDate
              const dg = k.diagnosis

              const kk = `${tp}/${ins}/${rem}/${inss}/${eps}/${dd}/${dg}`
              const kt = ktTags.indexOf(kk)
              if (kt === -1) {
                ktTags.push(kk)
                kts.push(k)
              }
            }
          )
          l.f.catheters = kts

          const bcs = []
          const bcTags = []
          l.f.bacteremias.forEach(
            b => {
              const eps = b.episod
              const dg = b.divDiag
              const og = b.origin
              const ent = b.entrance_v2
              const kt = b.catheterType_v2
              const ins = b.insertion

              const bk = `${eps}/${dg}/${og}/${ent}/${kt}/${ins}`
              const bt = bcTags.indexOf(bk)
              if (bt === -1) {
                bcTags.push(bk)
                bcs.push(b)
              }
            }
          )
          l.f.bacteremias = bcs

          const pns = []
          const pnTags = []
          l.f.pneumopathies.forEach(
            p => {
              const eps = p.episod
              const dg = p.diagnosis

              const pk = `${eps}/${dg}`
              const pt = pnTags.indexOf(pk)
              if (pt === -1) {
                pnTags.push(pk)
                pns.push(p)
              }
            }
          )
          l.f.pneumopathies = pns

          const k = `${l.sv}/${uf}/${bd}/${unit}/${sex}/${etd}/${exd}/${dth}/${org}/${trm}/${dg}/${imm}/${can}/${atb}/${igs}/${itb}/${uri}`
          const t = tags.indexOf(k)
          if (t === -1 && beds.val > 0) {
            tags.push(k)
            sheets.push(l)
          }
        }
      )

      const reaInfCSV = flatten(
        {
          type: 'reaInfADI',
          sheets: sheets,
          surveillances: surv,
          datasets: datasets,
          onlyClosed: true
        }
      )

      const files = [
        {
          s: reaInfCSV.icu.sheets,
          o: reaInfCSV.icu.options,
          f: fileName('ICU', '0', this.year)
        },
        {
          s: reaInfCSV.icuPt.sheets,
          o: reaInfCSV.icuPt.options,
          f: fileName('ICU_PT', '1', this.year)
        },
        {
          s: reaInfCSV.icuPtInf.sheets,
          o: reaInfCSV.icuPtInf.options,
          f: fileName('ICU_PT_INF', '3', this.year)
        },
        {
          s: reaInfCSV.icuPtExp.sheets,
          o: reaInfCSV.icuPtExp.options,
          f: fileName('ICU_PT_EXP', '2', this.year)
        },
        {
          s: reaInfCSV.icuPtInfRes.sheets,
          o: reaInfCSV.icuPtInfRes.options,
          f: fileName('ICU_PT_INF_RES', '4', vm.year)
        }
      ]
      this.saveToCSV(files, 0)
    },

    saveToCSV: function (f, i) {
      assert(i >= 0)
      if (i >= f.length) {
        this.active = false
        return
      }

      const fc = f[i]
      assert.notNull(fc.s)
      assert.notNull(fc.o)
      assert.notNull(fc.f)

      const vm = this
      stringify(
        fc.s,
        fc.o,

        function (err, output) {
          if (err != null) {
            vm.setError(`${err} (fichier ${fc.f})`)
            return
          }

          vm.target = {
            filename: fc.f,
            blob: new Blob([output], { type: 'text/plain' })
          }

          setTimeout(() => vm.saveToCSV(f, i + 1), 300)
        }
      )
    },

    setError: function (err) {
      this.active = false
      this.$notify({
        type: 'danger',
        title: 'Erreur !',
        content: `Impossible d'exporter les données: ${err}.`
      })
    }
  }
}
</script>
<style scoped lang="less">
.export-infections-data {
  .export-button {
    margin: 0.3rem;
  }
}
</style>
